/*
 * Periods for stats ("This quarter", "Last financial year", a custom range): days from and to, both
 * included, as "YYYY-MM-DD" in the business's own days. The choices follow accounting software
 * (Xero's): this, last and to date, by month, quarter and financial year. Quarters follow the
 * financial year, which starts in a month that depends on the country (financialYearStart).
 * Days are the business's, in its time zone (its ServiceM8 account's) wherever this runs: the
 * server's clock or the browser's may be in another. Only turning a time into a day (dayOf, todayIn)
 * or a day into a time (dateOf) needs the zone; counting days and months is plain calendar
 * arithmetic, the same anywhere. Shared by the server and the browser: no imports.
 */

export interface Period {
    /** First and last day, both included ("YYYY-MM-DD"). */
    from: string;
    to: string;
}

export type PresetId =
    | "last12"
    | "thisMonth"
    | "thisQuarter"
    | "thisYear"
    | "lastMonth"
    | "lastQuarter"
    | "lastYear"
    | "monthToDate"
    | "quarterToDate"
    | "yearToDate";

/** Ready-made periods, in groups (a line between each), as they're offered. */
export const PRESET_GROUPS: readonly (readonly PresetId[])[] = [
    ["last12"],
    ["thisMonth", "thisQuarter", "thisYear"],
    ["lastMonth", "lastQuarter", "lastYear"],
    ["monthToDate", "quarterToDate", "yearToDate"],
];

export const PRESET_LABELS: Record<PresetId, string> = {
    last12: "Last 12 months",
    thisMonth: "This month",
    thisQuarter: "This quarter",
    thisYear: "This financial year",
    lastMonth: "Last month",
    lastQuarter: "Last quarter",
    lastYear: "Last financial year",
    monthToDate: "Month to date",
    quarterToDate: "Quarter to date",
    yearToDate: "Year to date",
};

/** Months the financial year starts in, by country (1 = January); anywhere else, the calendar year. */
const FINANCIAL_YEAR_START: Record<string, number> = { AU: 7, NZ: 4, GB: 4, IN: 4, JP: 4, HK: 4, ZA: 3, PK: 7, EG: 7, KE: 7 };

/** The month a country's financial year starts in (1-12): January when not known. */
export const financialYearStart = (country: string | null) => (country ? (FINANCIAL_YEAR_START[country] ?? 1) : 1);

// ---------------------------------------------------------------------------------------------
// Time zones: an IANA name ("Australia/Sydney"); none (undefined) is the runtime's own.

/** Each zone's clock, read in parts (made once per zone: they're slow to make). */
const clocks = new Map<string, Intl.DateTimeFormat>();
function clockIn(zone: string | undefined) {
    const key = zone ?? "";
    let clock = clocks.get(key);
    if (!clock) {
        clock = new Intl.DateTimeFormat("en-US", { timeZone: zone, hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric" });
        clocks.set(key, clock);
    }
    return clock;
}

/** A zone the runtime knows (as given), else undefined: the runtime's own (the server's, or the browser's). */
export function knownZone(zone: string | null | undefined): string | undefined {
    if (!zone) return undefined;
    try {
        clockIn(zone);
        return zone;
    } catch {
        return undefined;
    }
}

type WallClock = Record<"year" | "month" | "day" | "hour" | "minute" | "second", number>;
const WALL_PARTS = new Set(["year", "month", "day", "hour", "minute", "second"]);

/** What a zone's clock read at a time: the date and time as numbers (month 1-12). */
function wallClock(at: Date, zone: string | undefined): WallClock {
    const parts: Partial<WallClock> = {};
    for (const p of clockIn(zone).formatToParts(at)) if (WALL_PARTS.has(p.type)) parts[p.type as keyof WallClock] = Number(p.value);
    return parts as WallClock;
}

/** How far a zone's clock is ahead of UTC at a time (ms; negative behind). */
function offsetAt(at: number, zone: string | undefined) {
    const c = wallClock(new Date(at), zone);
    // Midnight as hour 0 (some runtimes say 24).
    return Date.UTC(c.year, c.month - 1, c.day, c.hour % 24, c.minute, c.second) - Math.floor(at / 1000) * 1000;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** The day it is at a time ("YYYY-MM-DD") in a zone (knownZone; none: the runtime's). */
export function dayOf(at: Date, zone?: string) {
    const c = wallClock(at, zone);
    return `${c.year}-${pad(c.month)}-${pad(c.day)}`;
}

/** Today ("YYYY-MM-DD") in the business's zone (any: one the runtime doesn't know is the runtime's). */
export const todayIn = (zone: string | null | undefined, now = new Date()) => dayOf(now, knownZone(zone));

/**
 * When a day starts in a zone (its midnight there; none: the runtime's). Right across a change of
 * clocks: where midnight is skipped, the first time that's on the day.
 */
export function dateOf(day: string, zone?: string) {
    const wall = civil(day).getTime();
    const guess = wall - offsetAt(wall, zone);
    // The offsets either side of it (they differ when the clocks change near midnight): the earliest time that's on the day.
    const times = [...new Set([wall - offsetAt(guess - 6 * 3_600_000, zone), wall - offsetAt(guess + 6 * 3_600_000, zone)])].sort((a, b) => a - b);
    return new Date(times.find((t) => dayOf(new Date(t), zone) === day) ?? guess);
}

// ---------------------------------------------------------------------------------------------
// Calendar days: each a date at midnight UTC, so adding days and months is the same anywhere.

/** "YYYY-MM-DD" as a calendar date (not a time anyone's clock read). */
const civil = (day: string) => new Date(`${day}T00:00:00Z`);
/** A calendar date as "YYYY-MM-DD". */
const dayOfCivil = (d: Date) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
/** The first day of the month `months` after `year`/`month` (0-based month; any number of months). */
const monthStart = (year: number, month: number, months = 0) => new Date(Date.UTC(year, month + months, 1));
/** The day `days` after a date (before, when negative). */
const plusDays = (d: Date, days: number) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + days));

/**
 * A preset's days, as of `now` in the business's zone (`zone`: knownZone, any it doesn't know is
 * the runtime's), and the month its financial year starts in (1-12).
 */
export function presetPeriod(id: PresetId, now: Date, fyStart = 1, zone?: string | null): Period {
    const today = civil(todayIn(zone, now));
    const y = today.getUTCFullYear();
    const m = today.getUTCMonth();
    // The financial year and quarter `today` is in: how many months into the year, then each start.
    const intoYear = (m - (fyStart - 1) + 12) % 12;
    const yearStart = monthStart(y, m, -intoYear);
    const quarterStart = monthStart(y, m, -(intoYear % 3));
    const span = (from: Date, months: number): Period => ({ from: dayOfCivil(from), to: dayOfCivil(plusDays(monthStart(from.getUTCFullYear(), from.getUTCMonth(), months), -1)) });
    const toDate = (from: Date): Period => ({ from: dayOfCivil(from), to: dayOfCivil(today) });
    switch (id) {
        case "last12":
            // This month and the 11 before (as the Materials list's "not used in 12 months").
            return { from: dayOfCivil(monthStart(y, m, -11)), to: dayOfCivil(today) };
        case "thisMonth":
            return span(monthStart(y, m), 1);
        case "thisQuarter":
            return span(quarterStart, 3);
        case "thisYear":
            return span(yearStart, 12);
        case "lastMonth":
            return span(monthStart(y, m, -1), 1);
        case "lastQuarter":
            return span(monthStart(quarterStart.getUTCFullYear(), quarterStart.getUTCMonth(), -3), 3);
        case "lastYear":
            return span(monthStart(yearStart.getUTCFullYear(), yearStart.getUTCMonth(), -12), 12);
        case "monthToDate":
            return toDate(monthStart(y, m));
        case "quarterToDate":
            return toDate(quarterStart);
        case "yearToDate":
            return toDate(yearStart);
    }
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;
/** A real day ("2026-02-30" isn't). */
export const isDay = (s: string) => DAY.test(s) && dayOfCivil(civil(s)) === s;

/** Days in a period, both ends counted. */
export const daysIn = (p: Period) => Math.round((civil(p.to).getTime() - civil(p.from).getTime()) / 86_400_000) + 1;

/** The longest period stats are worked out for: about 10 years. */
export const MAX_PERIOD_DAYS = 3660;

/** Days that make a period stats can be asked for: both real, in order, and at most MAX_PERIOD_DAYS. */
export const isPeriod = (from: string, to: string) => isDay(from) && isDay(to) && from <= to && daysIn({ from, to }) <= MAX_PERIOD_DAYS;

/** A day as written (`locale`: the business's; e.g. { day: "numeric", month: "short" }: "19 Oct"), the same day whatever the zone. */
export const dayText = (day: string, locale?: string | null, options: Intl.DateTimeFormatOptions = {}) => civil(day).toLocaleDateString(locale ?? undefined, { ...options, timeZone: "UTC" });

/**
 * A period as written (`locale`: the business's): a whole month "Oct 2026", else "1 Oct - 31 Dec
 * 2026", with each end's year only when they differ ("1 Jul 2026 - 30 Jun 2027").
 */
export function periodText(p: Period, locale?: string | null): string {
    const from = civil(p.from);
    const wholeMonth = from.getUTCDate() === 1 && dayOfCivil(plusDays(monthStart(from.getUTCFullYear(), from.getUTCMonth(), 1), -1)) === p.to;
    if (wholeMonth) return dayText(p.from, locale, { month: "short", year: "numeric" });
    const sameYear = p.from.slice(0, 4) === p.to.slice(0, 4);
    const fromText = dayText(p.from, locale, { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) });
    const toText = dayText(p.to, locale, { day: "numeric", month: "short", year: "numeric" });
    return p.from === p.to ? toText : `${fromText} - ${toText}`;
}

/** How a period's use is shown as bars: by day (a month or less), by week (about a quarter or less), else by month. */
export type BarUnit = "day" | "week" | "month";
export function barUnit(p: Period): BarUnit {
    const days = daysIn(p);
    if (days <= 31) return "day";
    return days <= 98 ? "week" : "month";
}

/** The day `days` after one (before, when negative), as calendar days: right across a change of clocks (unlike adding 24 hours). */
export const addDays = (day: string, days: number) => dayOfCivil(plusDays(civil(day), days));

/** The first day of the bar after one starting on `d`. */
function nextBar(d: Date, unit: BarUnit) {
    if (unit === "month") return monthStart(d.getUTCFullYear(), d.getUTCMonth(), 1);
    return plusDays(d, unit === "day" ? 1 : 7);
}

/** The bars' first days across a period, oldest first: each day, each 7 days from its start, or each month (the first partly). */
export function barStarts(p: Period): string[] {
    const unit = barUnit(p);
    const starts: string[] = [];
    const end = civil(p.to).getTime();
    let d = civil(p.from);
    while (d.getTime() <= end) {
        starts.push(dayOfCivil(d));
        d = nextBar(d, unit);
    }
    return starts;
}
